import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import moment from 'moment';
import __ from 'utils/i18n';
import CardIcon from 'components/CardIcon';
import Loader from 'components/Loader';
import '../../../styles.scss';

export const ChargeDetailPanel = ({ charge, onClose, loadingAttempts }) => {
  if (!charge) return null;

  const formatCurrency = (amount, currency) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      'succeeded': 'success',
      'pending': 'warning',
      'processing': 'warning',
      'failed': 'danger',
      'cancelled': 'secondary',
      'pastdue': 'warning',
      'skippedzeroamount': 'secondary',
      'skippednullamount': 'danger',
    };
    return statusClasses[status] || 'secondary';
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'succeeded': __('dashboard.sections.recurringPayments.charges.table.statuses.succeeded'),
      'pending': __('dashboard.sections.recurringPayments.charges.table.statuses.pending'),
      'processing': __('dashboard.sections.recurringPayments.charges.table.statuses.processing'),
      'failed': __('dashboard.sections.recurringPayments.charges.table.statuses.failed'),
      'cancelled': __('dashboard.sections.recurringPayments.charges.table.statuses.cancelled'),
      'pastdue': __('dashboard.sections.recurringPayments.charges.table.statuses.pastdue'),
      'skippedzeroamount': __('dashboard.sections.recurringPayments.charges.table.statuses.skippedzeroamount'),
      'skippednullamount': __('dashboard.sections.recurringPayments.charges.table.statuses.skippednullamount'),
    };
    return statusTexts[status] || status;
  };

  const getCardBrandDisplay = (brand) => {
    if (!brand) return '';
    const normalizedBrand = brand.toLowerCase();
    const brandNames = {
      'visa': 'VISA',
      'mastercard': 'Mastercard',
      'amex': 'AMEX',
      'american express': 'AMEX',
    };
    return brandNames[normalizedBrand] || brand.toUpperCase();
  };

  const hasAttempts = charge.attempts && charge.attempts.length > 0;

  return (
    <div className="panel-flex">
      <div>
        <div className="recurring-payments-detail-container">
          {/* Header con monto total */}
          <div className="d-flex justify-content-between align-items-start mb-4 p-3 bg-light" style={{ borderRadius: '8px' }}>
            <div>
              <span className="text-muted text-wrap">{__('dashboard.sections.recurringPayments.charges.detail.total')}</span>
            </div>
            <div>
              <h1 className="mb-0 font-weight-bold text-wrap" style={{ fontSize: '2rem' }}>{formatCurrency(charge.amount, charge.currency)}</h1>
            </div>
          </div>

          {/* Información del suscriptor */}
          <div className="mb-4">
            <div className="font-weight-bold text-wrap">{charge.customerName || '-'}</div>
            <small className="text-muted text-wrap">{charge.customerEmail || '-'}</small>
            {charge.externalReference && (
              <div className="text-muted text-wrap" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                {__('dashboard.sections.recurringPayments.subscribers.detail.idRef')}: {charge.externalReference}
              </div>
            )}
          </div>

          {/* Plan */}
          <div className="mb-4">
            <div className="text-muted mb-1 ">{__('dashboard.sections.recurringPayments.charges.table.plan')}</div>
            <div className="text-wrap">{charge.planName || '-'}</div>
          </div>

          {/* Medio de pago */}
          <div className="mb-4">
            <div className="d-flex align-items-center">
              <CardIcon src={{ issuerName: charge.paymentMethodBrand }} />
              <div className="ml-3">
                <div className="font-weight-bold">
                  {getCardBrandDisplay(charge.paymentMethodBrand)} **** {charge.paymentMethodLast4 || '-'}
                </div>
                {charge.referenceNumber && (
                  <small className="text-muted text-wrap">
                    {__('dashboard.sections.recurringPayments.charges.detail.transaction')}: {charge.referenceNumber}
                  </small>
                )}
              </div>
            </div>
          </div>

          {/* Fecha y Estado */}
          <div className="mb-4 d-flex justify-content-between align-items-center">
            <div>
              {charge.createdAt ? moment(charge.createdAt).format('DD/MM/YYYY HH:mm') : '-'}
            </div>
            <span 
              className={`badge badge-${getStatusBadgeClass(charge.status)}`}
              style={{ fontSize: '0.75rem', padding: '0.4em 0.8em', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {getStatusText(charge.status)}
            </span>
          </div>
        </div>

        {/* Sección de historial de intentos - solo si está cargando o hay intentos */}
        {loadingAttempts && (
          <div className="recurring-payments-section mt-4">
            <div className="recurring-payments-section-header d-flex justify-content-between align-items-center p-3 mb-0">
              <h6 className="mb-0 font-weight-bold">
                {__('dashboard.sections.recurringPayments.charges.detail.attemptsHistory')}
              </h6>
            </div>
            <div className="py-4">
              <Loader />
            </div>
          </div>
        )}
        
        {!loadingAttempts && hasAttempts && (
          <div className="recurring-payments-section mt-4">
            <div className="recurring-payments-section-header d-flex justify-content-between align-items-center p-3 mb-0">
              <h6 className="mb-0 font-weight-bold">
                {__('dashboard.sections.recurringPayments.charges.detail.attemptsHistory')} ({charge.attempts.length})
              </h6>
            </div>
            <div className="pt-2">
              {charge.attempts.map((attempt, index) => {
                const isRejected = attempt.status === 'failed' || attempt.status === 'cancelled';
                const isSkipped = attempt.status === 'skippedzeroamount' || attempt.status === 'skippednullamount';
                const tooltipId = `attempt-tooltip-${attempt.attemptId || index}`;
                
                return (
                  <div 
                    key={attempt.attemptId || index} 
                    className="recurring-payments-list-item d-flex justify-content-between align-items-center py-3 px-2"
                  >
                    <div className="d-flex align-items-center">
                      <CardIcon src={{ issuerName: charge.paymentMethodBrand }} />
                      <div className="ml-3">
                        <div className="font-weight-bold">
                          **** {charge.paymentMethodLast4 || '-'}
                        </div>
                        <div className="text-muted small">
                          {attempt.attemptedAt ? moment(attempt.attemptedAt).format('DD/MM/YYYY') : '-'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-weight-bold mb-1 text-wrap">{formatCurrency(attempt.amount, attempt.currency)}</div>
                      <span className={`badge badge-${getStatusBadgeClass(attempt.status)} recurring-payments-badge-small`}>
                        {getStatusText(attempt.status)}
                        {(isRejected || isSkipped) && (
                          <>
                            <i 
                              id={tooltipId}
                              className="mdi mdi-information-outline ml-1"
                              style={{ fontSize: '12px', cursor: 'pointer' }}
                            ></i>
                            <UncontrolledTooltip placement="top" target={tooltipId}>
                              {isSkipped 
                                ? __(`dashboard.sections.recurringPayments.charges.table.tooltips.${attempt.status}`)
                                : attempt.failureReason
                              }
                            </UncontrolledTooltip>
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

